import { Controller } from "@hotwired/stimulus"
import { prefersReducedMotion } from "../helpers"

/**
 * Toast Controller
 * 
 * A reusable toast notification controller using flash-message styles.
 * Can be triggered via custom events dispatched to the document.
 * 
 * Usage via custom event:
 * document.dispatchEvent(new CustomEvent('toast:show', { 
 *   detail: { message: 'Success!', type: 'notice' }
 * }))
 * 
 * Parameters:
 * - message: The text to display
 * - type: 'notice' | 'alert' | 'warning' (default: 'notice') - matches Rails flash types
 * - duration: Time in ms before auto-dismiss (default: 4000)
 * 
 * HTML setup (add once to your layout):
 * <div data-controller="toast"></div>
 */
export default class extends Controller {
  static values = {
    duration: { type: Number, default: 4000 }
  }

  connect() {
    this._boundShowHandler = this._handleShowEvent.bind(this)
    document.addEventListener('toast:show', this._boundShowHandler)
  }

  disconnect() {
    document.removeEventListener('toast:show', this._boundShowHandler)
  }

  _handleShowEvent(event) {
    const { message, type, duration } = event.detail || {}
    this.show(message, type, duration)
  }

  show(message, type = 'notice', duration = this.durationValue) {
    // Map 'success' to 'notice' for convenience
    const flashType = type === 'success' ? 'notice' : type
    
    // Create container with flash-message-absolute-container styles
    const container = document.createElement('div')
    container.className = 'flash-message-absolute-container'
    
    // Create the flash message. `animate-slide-y-in` gives it the same entrance
    // as server-rendered flashes; the container handles the fade-out on dismiss.
    const toast = document.createElement('div')
    toast.className = `flash-message animate-slide-y-in ${flashType} flex items-center gap-3 rounded-lg`
    toast.innerHTML = `${this._getIcon(flashType)}<span>${message}</span>`
    
    container.appendChild(toast)
    document.body.appendChild(container)
    
    setTimeout(() => {
      // Under reduced motion, skip the fade-out tail and remove it at once.
      if (prefersReducedMotion()) {
        if (container.parentNode) {
          document.body.removeChild(container)
        }
        return
      }

      container.style.opacity = '0'
      container.style.transition = 'opacity 0.3s ease-out'
      setTimeout(() => {
        if (container.parentNode) {
          document.body.removeChild(container)
        }
      }, 300)
    }, duration)
  }

  _getIcon(type) {
    const icons = {
      notice: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>`,
      alert: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
      </svg>`,
      warning: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
      </svg>`
    }
    return icons[type] || icons.notice
  }
}

